import {Helmet} from 'react-helmet'
import {useTranslation} from '@/translations/i18n.tsx'
import {Container} from '@components/ui/container'
import {IconContainer} from '@components/ui/icon-container'
import {
    Home02Icon,
    Camera01Icon,
    TrendUp01Icon,
    Settings01Icon,
    ArrowRightIcon,
    User01Icon,
    Shield02Icon,
    ClockIcon
} from '@components/ui/icon'
import {Badge} from '@components/ui/badge'
import {ButtonLink} from '@components/commons/button-link'
import {routes} from '@utilities/constants/routes'
import {Footer} from '@components/commons/footer'
import LandingHero from '@/assets/landing/home_landing.png'
import AssignmentsImage from '@/assets/landing/assignments.png'
import InsightsImage from '@/assets/landing/insights.png'
import SettingsImage from '@/assets/landing/settings.png'

const TITLE_CLASSNAME = 'text-display-md font-bold text-neutral-900 m-0 text-center md:text-display-lg'
const SUBTITLE_CLASSNAME = 'text-lg text-neutral-600 m-0 text-center md:text-xl max-w-150'
const ICON_WRAPPER_CLASSNAME = 'flex items-center justify-center w-full h-full'

export const PublicLanding = () => {
    const {t} = useTranslation()

    return (
        <div className="bg-white min-h-screen">
            <Helmet title={t('titles:public_landing')} />

            {/* Hero Section */}
            <section className="bg-[linear-gradient(135deg,var(--color-primary-50)_0%,var(--color-blue-50)_100%)] py-12 min-h-[80vh] md:py-20 md:min-h-[90vh]">
                <div className="flex justify-center items-center min-h-full">
                    <Container>
                        <div className="flex flex-col items-center gap-12">
                            <div className="flex flex-col items-center gap-8 max-w-200 text-center">
                                <Badge variant="primary">{t('public_landing:hero_badge')}</Badge>
                                <h1 className="text-display-lg font-bold text-neutral-900 m-0 md:text-display-2xl">
                                    {t('public_landing:hero_title')}
                                </h1>
                                <p className="text-lg text-neutral-600 m-0 md:text-xl">
                                    {t('public_landing:hero_description')}
                                </p>
                                <div className="flex items-center gap-4">
                                    <ButtonLink
                                        size="lg"
                                        to={routes.LOGIN.handle.fullPath}
                                        className="text-md font-semibold bg-primary-600 text-white py-3 px-6 rounded-xl no-underline inline-flex items-center gap-2 transition-all duration-200 ease-in-out border-none hover:bg-primary-700 hover:translate-y-[-2px] hover:shadow-[0_8px_16px_rgba(0,0,0,0.15)] active:translate-y-0"
                                    >
                                        {t('public_landing:get_started')}
                                        <ArrowRightIcon />
                                    </ButtonLink>
                                </div>
                            </div>

                            <div className="flex justify-center">
                                <img
                                    className="max-w-full h-auto rounded-2xl shadow-[0_20px_40px_rgba(0,0,0,0.1)]"
                                    src={LandingHero}
                                    alt={t('public_landing:hero_image_alt')}
                                />
                            </div>
                        </div>
                    </Container>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-12 md:py-20">
                <Container>
                    <div className="flex flex-col items-center gap-12">
                        <div className="flex flex-col items-center gap-4">
                            <h2 className={TITLE_CLASSNAME}>{t('public_landing:features_title')}</h2>
                            <p className={SUBTITLE_CLASSNAME}>{t('public_landing:features_subtitle')}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:gap-8 md:grid-cols-2 md:gap-6">
                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <Home02Icon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_assignments_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_assignments_description')}
                                </p>
                            </div>

                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <Camera01Icon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_media_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_media_description')}
                                </p>
                            </div>

                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <TrendUp01Icon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_insights_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_insights_description')}
                                </p>
                            </div>

                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <Settings01Icon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_settings_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_settings_description')}
                                </p>
                            </div>

                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <Shield02Icon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_security_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_security_description')}
                                </p>
                            </div>

                            <div className="bg-white border border-neutral-200 rounded-2xl p-8 text-center transition-all duration-200 ease-in-out hover:translate-y-[-4px] hover:shadow-[0_16px_32px_rgba(0,0,0,0.1)] hover:border-primary-200">
                                <div className="w-16 h-16 rounded-2xl bg-neutral-100 mx-auto mb-4 [&_svg]:w-8 [&_svg]:h-8 [&_svg]:text-primary-600">
                                    <div className={ICON_WRAPPER_CLASSNAME}>
                                        <ClockIcon />
                                    </div>
                                </div>
                                <h3 className="text-display-xs font-semibold text-neutral-900 mt-4 mb-3 mx-0">
                                    {t('public_landing:feature_realtime_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:feature_realtime_description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* How It Works Section */}
            <section className="bg-neutral-50 py-12 md:py-20">
                <Container>
                    <div className="flex flex-col items-center gap-12">
                        <div className="flex flex-col items-center gap-4">
                            <h2 className={TITLE_CLASSNAME}>{t('public_landing:how_it_works_title')}</h2>
                            <p className={SUBTITLE_CLASSNAME}>{t('public_landing:how_it_works_subtitle')}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
                            <div className="bg-white rounded-2xl p-8 text-center relative border border-neutral-200">
                                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full bg-[linear-gradient(135deg,#f63d68_0%,#2e90fa_100%)] text-white text-sm font-bold">
                                    <div className={ICON_WRAPPER_CLASSNAME}>01</div>
                                </div>
                                <img
                                    className="w-full h-50 object-cover rounded-xl mb-4"
                                    src={AssignmentsImage}
                                    alt={t('public_landing:step_1_image_alt')}
                                />
                                <h3 className="text-display-xs font-semibold text-neutral-900 pt-4 pb-3 mx-0">
                                    {t('public_landing:step_1_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:step_1_description')}
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl p-8 text-center relative border border-neutral-200">
                                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full bg-[linear-gradient(135deg,#f63d68_0%,#2e90fa_100%)] text-white text-sm font-bold">
                                    <div className={ICON_WRAPPER_CLASSNAME}>02</div>
                                </div>
                                <img
                                    className="w-full h-50 object-cover rounded-xl mb-4"
                                    src={SettingsImage}
                                    alt={t('public_landing:step_2_image_alt')}
                                />
                                <h3 className="text-display-xs font-semibold text-neutral-900 pt-4 pb-3 mx-0">
                                    {t('public_landing:step_2_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:step_2_description')}
                                </p>
                            </div>

                            <div className="bg-white rounded-2xl p-8 text-center relative border border-neutral-200">
                                <div className="absolute -top-4 left-8 w-8 h-8 rounded-full bg-[linear-gradient(135deg,#f63d68_0%,#2e90fa_100%)] text-white text-sm font-bold">
                                    <div className={ICON_WRAPPER_CLASSNAME}>03</div>
                                </div>
                                <img
                                    className="w-full h-50 object-cover rounded-xl mb-4"
                                    src={InsightsImage}
                                    alt={t('public_landing:step_3_image_alt')}
                                />
                                <h3 className="text-display-xs font-semibold text-neutral-900 pt-4 pb-3 mx-0">
                                    {t('public_landing:step_3_title')}
                                </h3>
                                <p className="text-md text-neutral-600 m-0 leading-[1.6]">
                                    {t('public_landing:step_3_description')}
                                </p>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Testimonials Section */}
            <section className="py-12 md:py-20">
                <Container>
                    <div className="flex flex-col items-center gap-12">
                        <div className="flex flex-col items-center gap-4">
                            <h2 className={TITLE_CLASSNAME}>{t('public_landing:testimonials_title')}</h2>
                            <p className={SUBTITLE_CLASSNAME}>{t('public_landing:testimonials_subtitle')}</p>
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
                            <div className="flex flex-col gap-3 bg-white border border-neutral-200 rounded-2xl p-4 relative [&_svg]:text-warning-500 [&_svg]:w-4 [&_svg]:h-4">
                                <p className="text-lg text-neutral-700 leading-[1.6] italic">
                                    {t('public_landing:testimonial_1_quote')}
                                </p>
                                <div className="flex items-center gap-3">
                                    <IconContainer>
                                        <User01Icon />
                                    </IconContainer>
                                    <div>
                                        <h4 className="text-md font-semibold text-neutral-900 m-0">
                                            {t('public_landing:testimonial_1_author')}
                                        </h4>
                                        <span className="text-sm text-neutral-500">
                                            {t('public_landing:testimonial_1_role')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 bg-white border border-neutral-200 rounded-2xl p-4 relative [&_svg]:text-warning-500 [&_svg]:w-4 [&_svg]:h-4">
                                <p className="text-lg text-neutral-700 leading-[1.6] italic">
                                    {t('public_landing:testimonial_1_quote')}
                                </p>
                                <div className="flex items-center gap-3">
                                    <IconContainer>
                                        <User01Icon />
                                    </IconContainer>
                                    <div>
                                        <h4 className="text-md font-semibold text-neutral-900 m-0">
                                            {t('public_landing:testimonial_1_author')}
                                        </h4>
                                        <span className="text-sm text-neutral-500">
                                            {t('public_landing:testimonial_1_role')}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3 bg-white border border-neutral-200 rounded-2xl p-4 relative [&_svg]:text-warning-500 [&_svg]:w-4 [&_svg]:h-4">
                                <p className="text-lg text-neutral-700 leading-[1.6] italic">
                                    {t('public_landing:testimonial_1_quote')}
                                </p>
                                <div className="flex items-center gap-3">
                                    <IconContainer>
                                        <User01Icon />
                                    </IconContainer>
                                    <div>
                                        <h4 className="text-md font-semibold text-neutral-900 m-0">
                                            {t('public_landing:testimonial_1_author')}
                                        </h4>
                                        <span className="text-sm text-neutral-500">
                                            {t('public_landing:testimonial_1_role')}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            <Footer />
        </div>
    )
}
